import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check, ArrowLeft, ShieldCheck, Loader, AlertCircle, Crown,
  Calendar, CreditCard,
} from 'lucide-react';
import { billingService } from '../../services/billingService';
import { authService } from '../../services/authService';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  const { plan, billingCycle, serviceType } = location.state ?? {};

  if (!plan || !serviceType) {
    navigate('/portal/products', { replace: true });
    return null;
  }

  const isCmms = serviceType === 'CMMS';
  const price = billingCycle === 'annual'
    ? Number(plan.annualPrice) || 0
    : Number(plan.monthlyPrice) || 0;
  const priceLabel = billingCycle === 'annual' ? t('billing.perYear') : t('billing.perMonth');
  const backPath = isCmms ? '/portal/cmms/plans' : '/portal/chatbot/plans';

  const features = [];
  if (isCmms) {
    if (plan.includedUsers != null)     features.push(t('cmms.plns_upToUsers', { count: plan.includedUsers }));
    if (plan.includedAssets != null)    features.push(t('cmms.plns_upToAssets', { count: plan.includedAssets }));
    if (plan.includedLocations != null) features.push(t('cmms.plns_upToLocations', { count: plan.includedLocations }));
    if (plan.includedBranches != null)  features.push(t('cmms.plns_upToBranches', { count: plan.includedBranches }));
  } else {
    if (plan.messagesPerMonth != null)
      features.push(t('checkout.msgsPerMonth', { count: Number(plan.messagesPerMonth).toLocaleString() }));
    if (plan.maxChatbots != null)
      features.push(plan.maxChatbots > 1
        ? t('checkout.upToChatbots', { count: plan.maxChatbots })
        : t('checkout.upToChatbot', { count: plan.maxChatbots }));
    if (plan.maxDocumentsPerChatbot != null)
      features.push(plan.maxDocumentsPerChatbot === 1
        ? t('checkout.upToDocument', { count: plan.maxDocumentsPerChatbot })
        : t('checkout.upToDocuments', { count: plan.maxDocumentsPerChatbot }));
    if (plan.includedAgents != null)
      features.push(plan.includedAgents === 1
        ? t('checkout.includedAgent', { count: plan.includedAgents })
        : t('checkout.includedAgents', { count: plan.includedAgents }));
  }
  const allFeatures = [...features, ...(Array.isArray(plan.features) ? plan.features : [])];

  const handleConfirm = async () => {
    const user = authService.getCurrentUser();
    const companyId = user?.companyId;
    if (!companyId) {
      setError(t('checkout.errorNoCompany'));
      return;
    }
    try {
      setConfirming(true);
      setError(null);
      await billingService.activateSubscription(companyId, {
        serviceType,
        planId: plan.id,
        billingPeriod: billingCycle.toUpperCase(),
        companyEmail: user?.email,
        companyName: user?.companyName,
      });
      localStorage.setItem(
        isCmms ? 'hasCmmsSubscription' : 'hasChatbotSubscription',
        'true',
      );
      navigate('/portal/billing/success', { replace: true });
    } catch (err) {
      console.error('Activation error:', err);
      setError(t('checkout.errorActivation'));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate(backPath)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 text-sm font-medium transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('checkout.backToPlans')}
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('checkout.title')}</h1>
      <p className="text-gray-500 text-sm mb-8">
        {t('checkout.subtitle', { service: isCmms ? 'CMMS' : 'AI Chatbot' })}
      </p>

      <div className="grid md:grid-cols-3 gap-8 items-start">

        {/* ── Left: details ─────────────────────────────────────────────────── */}
        <div className="md:col-span-2 space-y-5">

          {/* Plan summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${
                isCmms ? 'from-primary-500 to-blue-600' : 'from-purple-500 to-purple-700'
              }`}>
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t('checkout.planLabel', { service: isCmms ? 'CMMS' : 'AI Chatbot' })}
                </p>
                <h2 className="text-xl font-bold text-gray-900 leading-tight">
                  {plan.planName || plan.name}
                </h2>
              </div>
            </div>

            {plan.description && (
              <p className="text-sm text-gray-600 mb-5">{plan.description}</p>
            )}

            {allFeatures.length > 0 && (
              <div className="border-t border-gray-100 pt-5">
                <p className="text-sm font-semibold text-gray-800 mb-3">{t('checkout.whatsIncluded')}</p>
                <div className="grid sm:grid-cols-2 gap-y-2 gap-x-4">
                  {allFeatures.map((f, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isCmms && plan.overageEnabled && plan.overagePricePerMessage != null && (
              <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-800 mb-1">{t('checkout.additionalUsage')}</p>
                <p className="text-xs text-amber-700">
                  {t('checkout.overageMsg', { price: Number(plan.overagePricePerMessage).toFixed(5) })}
                </p>
              </div>
            )}
          </div>

          {/* Billing breakdown */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <Calendar className="w-4 h-4 text-gray-400" />
              <h3 className="font-semibold text-gray-900">{t('checkout.billingBreakdown')}</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('checkout.billingPeriod')}</span>
                <span className="font-medium text-gray-900">{billingCycle === 'annual' ? t('billing.annual') : t('billing.monthly')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{plan.planName || plan.name}</span>
                <span className="font-medium text-gray-900">${price}{priceLabel}</span>
              </div>
              {billingCycle === 'annual' && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('checkout.effectiveMonthly')}</span>
                  <span className="font-medium text-gray-900">{t('checkout.effectiveMonthlyValue', { price: (price / 12).toFixed(2) })}</span>
                </div>
              )}
              <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-base">
                <span className="text-gray-900">{t('checkout.totalDue')}</span>
                <span className="text-gray-900">${price}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: confirm card ────────────────────────────────────────────── */}
        <div className="sticky top-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">

            {/* Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-5">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 leading-relaxed">
                  {t('checkout.paymentNotice')}
                </p>
              </div>
            </div>

            {/* Price */}
            <div className="text-center mb-5 pb-5 border-b border-gray-100">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{t('checkout.dueToday')}</p>
              <p className="text-4xl font-bold text-gray-900">${price}</p>
              <p className="text-sm text-gray-400 mt-1">{priceLabel}</p>
            </div>

            {/* Order details */}
            <div className="space-y-2 mb-5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('checkout.serviceLabel')}</span>
                <span className="font-medium text-gray-800">{isCmms ? 'CMMS' : 'AI Chatbot'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('checkout.planFieldLabel')}</span>
                <span className="font-medium text-gray-800">{plan.planName || plan.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('checkout.billingLabel')}</span>
                <span className="font-medium text-gray-800">{billingCycle === 'annual' ? t('billing.annual') : t('billing.monthly')}</span>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            {/* Confirm */}
            <button
              onClick={handleConfirm}
              disabled={confirming}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-60 ${
                isCmms
                  ? 'bg-primary-600 hover:bg-primary-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {confirming ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  {t('checkout.activating')}
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  {t('checkout.confirmActivate')}
                </>
              )}
            </button>

            <button
              onClick={() => navigate(backPath)}
              disabled={confirming}
              className="w-full mt-3 py-2 px-4 rounded-lg text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('checkout.cancel')}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Checkout;
